import { useState } from 'react';
import PropTypes from 'prop-types';

import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import ShowPasswordButton from 'dibs-buyer-layout/exports/ShowPasswordButton';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { hasPasswordError } from 'dibs-buyer-layout/exports/validatePassword';
import { Input } from 'dibs-elements/exports/Input';
import { Link } from 'dibs-elements/exports/Link';

import { EMAIL_ERRORS } from '../../../constants/checkoutConstants';

import styles from './styles/Password.scss';

const messages = defineMessages({
    placeholder: {
        id: 'checkout.Password.placeholder',
        defaultMessage: 'Create Password*',
    },
    fullAccountPlaceholder: {
        id: 'checkout.Password.fullAccountPlaceholder',
        defaultMessage: 'Enter Password*',
    },
    optionalLogIn: {
        id: 'checkout.Password.optionalLogin',
        defaultMessage: 'Password',
    },
});

const forgotPassword = (
    <FormattedMessage id="checkout.Password.forgort" defaultMessage="Forgot Password?" />
);

const invalidMessage = (
    <FormattedMessage
        id="checkout.Password.invalid"
        defaultMessage="The email/password combination does not match our records. {link}"
        values={{
            link: (
                <Link
                    onClick={e => {
                        e.preventDefault();
                        authModalLoader.show({
                            action: authModalLoader.constants.REQUEST_PASSWORD_EMAIL,
                            flow: authModalLoader.constants.EMAIL_LINK_LOGIN_FLOW,
                        });
                    }}
                >
                    {forgotPassword}
                </Link>
            ),
        }}
    />
);

const credentialsLeaked = (
    <FormattedMessage
        id="checkout.Password.credentialsLeaked"
        defaultMessage="This username and password combination is suspected to be compromised. Please create a new password."
    />
);

const Password = ({
    name = 'password',
    value = '',
    error = '',
    isBlurred,
    onChange,
    onBlur,
    isFullAccount,
    isLogIn,
    disabled = false,
}) => {
    const intl = useIntl();
    const [isPasswordHidden, setIsPasswordHidden] = useState(true);
    let errorMessage;
    if (error === EMAIL_ERRORS.USER_EXISTS) {
        errorMessage = invalidMessage;
    } else if (error === EMAIL_ERRORS.CREDENTIALS_ARE_LEAKED) {
        errorMessage = credentialsLeaked;
    }
    // TODO placeholder will just be optionalLogIn after disabler cleanup ENGAGE-1259
    // Any other registration logic can be removed. Button will only be used for login
    let placeholder;
    if (isLogIn) {
        placeholder = messages.optionalLogIn;
    } else if (isFullAccount) {
        placeholder = messages.fullAccountPlaceholder;
    } else {
        placeholder = messages.placeholder;
    }

    return (
        <Input
            type={isPasswordHidden ? 'password' : 'text'}
            name={name}
            value={value}
            pattern="[^ @]*@[^ @]*"
            autoComplete="new-password"
            placeholder={intl.formatMessage(placeholder)}
            hasAnimatedPlaceholder
            errorMessage={isBlurred && errorMessage}
            hasError={isBlurred && hasPasswordError(error)}
            dataTn="register-password"
            onChange={event => onChange({ field: name, value: event.target.value })}
            onBlur={event => onBlur({ event, field: name, value: event.target.value })}
            maskForPrivacy
            disabled={disabled}
            rightDecorator={
                <ShowPasswordButton
                    isPasswordHidden={isPasswordHidden}
                    setIsPasswordHidden={setIsPasswordHidden}
                />
            }
            rightDecoratorClass={styles.showPasswordButtonWrapper}
        />
    );
};

Password.propTypes = {
    name: PropTypes.string,
    value: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
    isBlurred: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    isFullAccount: PropTypes.bool,
    isLogIn: PropTypes.bool,
    disabled: PropTypes.bool,
    viewer: PropTypes.object,
};

export default Password;
